import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Fingerprint, Link2, Unlink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Sovereign ID — shows which sign-in methods reach this one account, and lets
 * a Hide-My-Email user attach their Apple ID after signing in the original
 * way (a relay address can never auto-match an existing account).
 */
export default function AppleIdentityCard() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");

  const claimEmail = async () => {
    setBusy(true);
    try {
      await api.post("/auth/apple/claim-email", { email: email.trim().toLowerCase() });
      toast.success("Email saved");
      setEmail("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const load = async () => {
    try { const { data } = await api.get("/auth/apple/link-status"); setSt(data); }
    catch { setSt(null); }
  };

  useEffect(() => {
    load();
    if (new URLSearchParams(window.location.search).get("apple_linked")) {
      toast.success("Apple ID linked — both sign-in methods now reach this account");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  if (!st) return null;

  const startLink = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/auth/me");
      window.location.href = `${API}/auth/apple/start?mode=link&link_user_id=${data.id}`;
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };

  const unlink = async () => {
    if (!window.confirm("Detach your Apple ID from this account?")) return;
    setBusy(true);
    try { await api.post("/auth/apple/unlink"); toast.success("Apple ID detached"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="hard-card p-6 mt-8" data-testid="apple-identity-section">
      <div className="overline mb-2 flex items-center gap-2">
        <Fingerprint className="w-4 h-4" /> § SIGN-IN METHODS · SOVEREIGN ID
      </div>
      <p className="font-mono text-xs text-muted-foreground max-w-2xl mb-4">
        Every sign-in method below reaches this same account, company and billing record — your
        subscription and loyalty pricing do not change based on which button you press. If you use
        Apple's Hide My Email, link it here after signing in the usual way, because a relay address
        can never be matched to an existing account automatically.
      </p>
      <div className="font-mono text-xs mb-4 flex flex-wrap gap-2" data-testid="apple-providers">
        {(st.providers || []).map((p) => (
          <span key={p} className="border-2 border-ink px-2 py-1 uppercase tracking-widest text-[10px]">{p}</span>
        ))}
        {st.linked && (
          <span className="border-2 border-ink bg-ink text-white px-2 py-1 uppercase tracking-widest text-[10px]"
                data-testid="apple-linked-badge">apple</span>
        )}
      </div>
      {st.linked ? (
        <div>
          {st.email_missing && (
            <div className="border-2 border-warn bg-warn/10 p-3 mb-4" data-testid="apple-email-missing">
              <div className="font-mono text-xs mb-2">
                Apple didn't share an email address with us, so we have a placeholder on file. Add a real
                one so pay-stub notices and filing reminders can reach you.
              </div>
              <div className="flex flex-wrap gap-2">
                <input data-testid="apple-claim-email-input" value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
                  className="px-3 py-2 border-2 border-ink font-mono text-xs flex-1 min-w-[200px]" />
                <button data-testid="apple-claim-email-btn" disabled={busy || !email}
                  onClick={claimEmail}
                  className="hard-btn px-4 py-2 font-mono uppercase text-xs tracking-widest disabled:opacity-40">
                  Save email
                </button>
              </div>
            </div>
          )}
          <div className="font-mono text-xs mb-3" data-testid="apple-linked-email">
            Linked Apple ID: {st.apple_email || "—"}
            {st.is_relay && <span className="ml-2 text-muted-foreground">(Hide My Email relay)</span>}
          </div>
          <button data-testid="apple-unlink-btn" disabled={busy} onClick={unlink}
            className="border-2 border-bad text-bad px-4 py-2 font-mono uppercase text-xs tracking-widest hover:bg-bad hover:text-white inline-flex items-center gap-2 disabled:opacity-40">
            <Unlink className="w-4 h-4" /> Detach Apple ID
          </button>
        </div>
      ) : (
        <div>
          <button data-testid="apple-link-btn" disabled={busy || !st.configured} onClick={startLink}
            className="border-2 border-ink bg-black text-white px-4 py-2 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 disabled:opacity-40">
            <Link2 className="w-4 h-4" /> Link my Apple ID
          </button>
          {!st.configured && (
            <div className="mt-2 font-mono text-[10px] text-muted-foreground" data-testid="apple-link-pending">
              Available as soon as the Apple developer keys are added.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
